import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterStellenberg() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-stellenberg",
    name: "Painters Stellenberg",
    location: "Stellenberg, Cape Town",
    rating: 4.8,
    reviews: 153,
    specialties: ["Residential Estates", "Luxury Homes", "Interior Design", "Exterior Protection"],
    image: "https://public.readdy.ai/ai/img_res/48ab25813dad9f6d3cecf559d7eec148.jpg",
    verified: true,
    yearsExperience: 15,
    website: "https://paintersstellenberg.co.za",
    email: "info@paintersstellenberg.co.za",
    description: "Professional painting services in Stellenberg and surrounding Northern Suburbs. We specialize in luxury residential estates and family homes with expertise in premium finishes and comprehensive property maintenance solutions.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Luxury Home Painting",
      "Estate Property Painting",
      "Roof Painting & Coatings",
      "Waterproofing Services",
      "Damp Proofing Solutions",
      "Color Consultation & Design"
    ],
    serviceAreas: ["Stellenberg", "Bellville", "Durbanville", "Welgemoed", "Panorama", "Parow", "Goodwood", "Northern Suburbs"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Stellenberg%2C%20luxury%20home%20with%20fresh%20white%20walls%2C%20quality%20paint%20finish%2C%20mountain%20view%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work%2C%20elegant%20design&width=400&height=300&seq=stellenberg-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Stellenberg%20Cape%20Town%2C%20luxury%20estate%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20upscale%20residential%20setting%2C%20well-maintained%20property&width=400&height=300&seq=stellenberg-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Residential%20estate%20painting%20project%20in%20Stellenberg%2C%20professional%20painters%20working%20on%20family%20home%20exterior%2C%20quality%20residential%20paint%20finish%2C%20modern%20elegant%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=stellenberg-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Kitchen%20interior%20painting%20in%20Stellenberg%20luxury%20home%2C%20fresh%20painted%20walls%2C%20modern%20kitchen%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=stellenberg-gallery-4&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Master%20bedroom%20interior%20painting%20in%20Stellenberg%20estate%20home%2C%20fresh%20painted%20walls%2C%20elegant%20bedroom%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=stellenberg-gallery-5&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20facade%20painting%20in%20Stellenberg%20residential%20estate%2C%20professional%20painters%20working%20on%20luxury%20home%2C%20quality%20exterior%20finish%2C%20modern%20architecture%2C%20pristine%20work%20environment&width=400&height=300&seq=stellenberg-gallery-6&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Robert Williams",
        rating: 5,
        comment: "Outstanding work on our Stellenberg home. The team was professional, punctual, and the quality of the finish is exceptional. They transformed our house and we couldn't be happier with the results."
      },
      {
        name: "Catherine du Plessis", 
        rating: 5,
        comment: "Excellent service from start to finish. They completed our estate home painting with great attention to detail and the color consultation was invaluable. Highly recommend for Stellenberg properties."
      },
      {
        name: "David Mitchell",
        rating: 5,
        comment: "Professional painters who truly understand luxury home requirements. They were clean, efficient, and the quality exceeded our expectations. Our home looks absolutely stunning."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Stellenberg | Professional Residential Painting Services Cape Town | Luxury Home Specialists";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Stellenberg offers professional painting services for luxury homes and residential estates in Stellenberg, Cape Town. Specializing in premium finishes, family homes, and comprehensive property maintenance. Get a free quote today.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Stellenberg, house painters Northern Suburbs, luxury home painting Cape Town, residential estate painting, premium painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20luxury%20residential%20home%20in%20Stellenberg%20Cape%20Town%2C%20upscale%20estate%20with%20Tygerberg%20Hills%20mountain%20backdrop%2C%20painters%20in%20clean%20uniforms%20with%20premium%20equipment%2C%20modern%20elegant%20architecture%20with%20manicured%20gardens%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20project&width=1200&height=600&seq=stellenberg-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Professional Painting Services in Stellenberg</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description}
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Luxury Residential Painting Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Stellenberg is one of Cape Town's most prestigious residential areas, known for its beautiful family homes, well-maintained estates, and strong sense of community. Our team has over 15 years of specialized experience working in this distinguished neighborhood, understanding the high standards expected by Stellenberg homeowners and the unique requirements of luxury residential properties. We've built our reputation on delivering exceptional painting services that enhance property value while providing long-lasting protection and beauty.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  The homes in Stellenberg represent significant investments, and proper maintenance is essential to preserving and enhancing their value. Our comprehensive painting services are designed to protect your property from the elements while creating beautiful living spaces that reflect your personal style and taste. We understand that your home is more than just a building – it's where your family creates memories and builds their lives. That's why we approach every project with the care, attention to detail, and professionalism that Stellenberg homeowners deserve and expect.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comprehensive Residential Estate Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Stellenberg's residential estates require painting contractors who understand the specific requirements of estate living, including adherence to architectural guidelines, coordination with homeowners associations, and the need for minimal disruption to neighbors. Our team is experienced in working within these parameters, ensuring all work meets estate standards while completing projects efficiently and professionally. We maintain clear communication with estate management and homeowners throughout the project, providing regular updates and addressing any concerns promptly.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  We offer comprehensive painting solutions for both interior and exterior projects, from single room refreshes to complete home transformations. Our painters are skilled in working with the architectural styles common in Stellenberg, including contemporary designs, traditional Cape Dutch influences, and modern family homes. We understand the importance of maintaining the aesthetic harmony of the neighborhood while allowing each home to express its unique character and the personality of its owners.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Premium Materials and Expert Craftsmanship</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Luxury homes deserve premium materials and expert application. We exclusively use top-tier paints and coatings from leading manufacturers, selected specifically for their superior coverage, durability, and finish quality. Our paint selection includes products with advanced features such as stain resistance, washability, and fade resistance, ensuring your home maintains its beautiful appearance for years to come. For interior spaces, we prioritize low-VOC and environmentally friendly products that are safe for your family while providing excellent performance.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our team consists of highly skilled painters with extensive experience in luxury residential projects. We invest in ongoing training to ensure our painters are familiar with the latest techniques, products, and industry best practices. This commitment to excellence means you can trust that your Stellenberg home is in capable hands, and the finished result will meet the highest standards of quality and craftsmanship. We take pride in our work and stand behind every project with comprehensive warranties and ongoing support.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Color Consultation and Design Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Choosing the right colors for your home can be challenging, with countless options available and the need to consider factors such as lighting, architectural features, and personal preferences. Our professional color consultation services take the stress out of this decision, providing expert guidance based on years of experience and a deep understanding of how colors work in different spaces and lighting conditions. We help you select palettes that enhance your home's architectural features, create the desired atmosphere in each room, and reflect your personal style.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our color consultants work closely with you to understand your vision and preferences, then provide recommendations that bring that vision to life. We consider factors such as the orientation of rooms, natural light patterns throughout the day, existing furnishings and finishes, and current design trends while ensuring the selections will remain timeless and appealing for years to come. We can also coordinate with interior designers and architects if you're working with design professionals, ensuring seamless integration of painting work into your overall design plan.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comprehensive Property Protection</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Beyond aesthetic enhancement, painting plays a crucial role in protecting your property from weather damage, moisture penetration, and general wear and tear. Our exterior painting services include comprehensive surface preparation, treatment of any existing damage, application of appropriate primers and sealers, and multiple coats of premium weather-resistant paint. We pay special attention to areas most vulnerable to weather exposure, ensuring complete protection for your home's exterior surfaces.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-8">
                  We also offer specialized services such as roof painting and coating, waterproofing, and damp proofing to provide complete protection for your Stellenberg property. Our roof coatings not only enhance appearance but also provide additional insulation and weather protection, helping to reduce energy costs and extend roof life. Waterproofing and damp proofing services are particularly important in Cape Town's climate, preventing moisture-related damage that can compromise structural integrity and create unhealthy living conditions. Our comprehensive approach ensures your home is protected from all angles.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20luxury%20home%20in%20Stellenberg%20Cape%20Town%2C%20upscale%20residential%20estate%20with%20mountain%20backdrop%2C%20painters%20in%20clean%20uniforms%20with%20quality%20equipment%2C%20modern%20elegant%20architecture%2C%20bright%20natural%20lighting&width=400&height=300&seq=stellenberg-sidebar-image&orientation=landscape"
                      alt="Professional painters Stellenberg luxury house painting services Cape Town residential estates"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Painting Services in Stellenberg</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Luxury Residential Painting Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our painting services are specifically designed for the unique requirements of Stellenberg's luxury residential properties. From interior room refreshes to complete exterior transformations, we provide comprehensive solutions that combine premium materials with expert craftsmanship and meticulous attention to detail.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with premium materials and expert residential application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Interior Painting Excellence</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our interior painting services transform Stellenberg homes into beautiful, comfortable living spaces. We use premium low-VOC paints that are safe for your family while providing excellent coverage and durability. Our color consultation services help you choose the perfect palette that enhances your home's architectural features and creates the desired atmosphere in each room.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We understand the importance of protecting your home and belongings during painting projects. Our team takes extensive precautions to protect floors, furniture, and fixtures, maintaining clean work areas throughout the project. We work efficiently to minimize disruption to your daily life while delivering exceptional results that exceed your expectations.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exterior Protection and Beauty</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Exterior painting in Stellenberg requires premium materials and expert application to withstand Cape Town's variable climate. We use weather-resistant paints and protective coatings that provide excellent durability while maintaining beautiful appearance. Our comprehensive exterior painting process includes thorough surface preparation, appropriate primer application, and multiple coats of premium paint for maximum protection.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We also offer specialized services such as roof painting and coating, waterproofing, and damp proofing to provide complete protection for your property. Our roof coatings enhance appearance while providing additional insulation and weather protection. Waterproofing and damp proofing services prevent moisture-related damage, ensuring your home remains structurally sound and healthy for your family.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Stellenberg</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Stellenberg and surrounding Northern Suburbs. Each project demonstrates our commitment to quality workmanship and our expertise in luxury residential painting applications.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Stellenberg Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional residential painting with premium materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Stellenberg have to say about our painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from homeowners in the area.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Stellenberg</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to transform your Stellenberg property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, long-lasting results that enhance your home and lifestyle.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding Northern Suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Quote Today</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Stellenberg painting project. Our experienced team will assess your property and provide detailed recommendations tailored to luxury residential requirements.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
